import {
    CHAT_LOAD_THREAD_SENDING,
    CHAT_LOAD_THREAD_SUCCESS,
    CHAT_LOAD_THREAD_FAILURE,
    CHAT_SEND_START,
    CHAT_THREAD_READY,
    CHAT_TOKEN,
    CHAT_TOOL_START,
    CHAT_TOOL_END,
    CHAT_STREAM_DONE,
    CHAT_STREAM_ERROR,
    CHAT_ABORTED,
    CHAT_NEW_THREAD,
    CHAT_CLEAR,
    CHAT_SET_AVAILABILITY,
} from '../constants/chatConstants';

const initialState = {
    threadId: null,
    messages: [],
    isLoadingHistory: false,
    isStreaming: false,
    activeTool: null,
    error: null,
    isAvailable: true,
};

/** The assistant bubble currently being streamed into is always the last one. */
const appendToLastAssistant = (messages, delta) => {
    const next = [...messages];
    const last = next[next.length - 1];
    if (last?.role === 'assistant' && last.status === 'streaming') {
        // Drop leading whitespace so a model that opens with "\n\n" does not
        // create a visually empty bubble before the real text arrives.
        const content = last.content === '' ? delta.replace(/^\s+/, '') : last.content + delta;
        next[next.length - 1] = { ...last, content };
    }
    return next;
};

const chatReducer = (state = initialState, action) => {
    switch (action.type) {
        case CHAT_LOAD_THREAD_SENDING:
            return { ...state, isLoadingHistory: true, error: null };

        case CHAT_LOAD_THREAD_SUCCESS:
            return {
                ...state,
                isLoadingHistory: false,
                threadId: action.threadId,
                messages: action.messages,
                error: null,
            };

        case CHAT_LOAD_THREAD_FAILURE:
            return { ...state, isLoadingHistory: false, error: action.error };

        case CHAT_SEND_START:
            return {
                ...state,
                isStreaming: true,
                error: null,
                activeTool: null,
                messages: [
                    ...state.messages,
                    { id: action.localId, role: 'user', content: action.message, status: 'sent' },
                    { id: `${action.localId}-reply`, role: 'assistant', content: '', status: 'streaming' },
                ],
            };

        case CHAT_THREAD_READY:
            return { ...state, threadId: action.threadId };

        case CHAT_TOKEN:
            return { ...state, messages: appendToLastAssistant(state.messages, action.delta) };

        case CHAT_TOOL_START:
            return { ...state, activeTool: { toolName: action.toolName, label: action.label } };

        case CHAT_TOOL_END:
            return { ...state, activeTool: null };

        case CHAT_STREAM_DONE: {
            const messages = [...state.messages];
            const last = messages[messages.length - 1];
            if (last?.role === 'assistant') {
                messages[messages.length - 1] = {
                    ...last,
                    id: action.messageId ?? last.id,
                    // Trust the server's assembled text over the accumulated deltas.
                    content: action.content || last.content,
                    status: 'done',
                };
            }
            return { ...state, isStreaming: false, activeTool: null, messages };
        }

        case CHAT_STREAM_ERROR: {
            const messages = [...state.messages];
            const last = messages[messages.length - 1];
            // Drop an assistant bubble that never received any text.
            if (last?.role === 'assistant' && last.status === 'streaming' && !last.content) {
                messages.pop();
            } else if (last?.role === 'assistant') {
                messages[messages.length - 1] = { ...last, status: 'done' };
            }
            return { ...state, isStreaming: false, activeTool: null, messages, error: action.error };
        }

        case CHAT_ABORTED: {
            const messages = [...state.messages];
            const last = messages[messages.length - 1];
            if (last?.role === 'assistant' && last.status === 'streaming') {
                if (last.content) messages[messages.length - 1] = { ...last, status: 'done' };
                else messages.pop();
            }
            return { ...state, isStreaming: false, activeTool: null, messages };
        }

        case CHAT_NEW_THREAD:
            return { ...initialState, isAvailable: state.isAvailable, threadId: action.threadId ?? null };

        case CHAT_SET_AVAILABILITY:
            return { ...state, isAvailable: action.isAvailable };

        case CHAT_CLEAR:
            return { ...initialState };

        default:
            return state;
    }
};

export default chatReducer;
